import React, { useCallback, useState } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';

import BaseExerciseQuestionsForm, { useQuestionsCountTextField } from 'modules/exercises/components/BaseExerciseQuestionForm';

import { MultipleChoiceExerciseInput, MultipleChoiceQuestionInput, MultipleChoiceOptionInput, MultipleChoiceOptionType } from '../../types/form';

import MultipleChoiceQuestionField from '../MultipleChoiceQuestionField';

interface MultipleChoiceQuestionsFormProps {
  initialQuestionNumber: number;
  onChangeInitialQuestionNumber: (value: number) => void;

  getNextQuestion: (index: number) => MultipleChoiceQuestionInput;
  getNextOption: (index: number) => MultipleChoiceOptionInput;
  canAddMoreOptions: (count: number) => boolean;

  allOptionTypes: MultipleChoiceOptionType[];
  defaultOptionType: MultipleChoiceOptionType['value'];
}

const MultipleChoiceQuestionsForm = (props: MultipleChoiceQuestionsFormProps) => {
  const {
    getNextOption,
    getNextQuestion,
    allOptionTypes,
    defaultOptionType,
    canAddMoreOptions,
    initialQuestionNumber,
    onChangeInitialQuestionNumber,
  } = props;

  const { control } = useFormContext<MultipleChoiceExerciseInput>();
  const { fields, append, remove } = useFieldArray({ control, name: 'questions' });
  const questionsCount = fields.length;
  const [questionsCountTextFieldValue, setQuestionsCountTextFieldValue] = useState(`${questionsCount}`);

  const handleAddQuestion = useCallback(() => {
    append(getNextQuestion(fields.length));
    setQuestionsCountTextFieldValue(`${fields.length + 1}`);
  }, [append, getNextQuestion, fields]);

  const handleRemoveQuestionAtIndex = useCallback(
    (index: number | number[] | undefined) => {
      remove(index);
      setQuestionsCountTextFieldValue(`${fields.length - 1}`);
    },
    [remove, fields]
  );

  const { onChangeQuestionsCount } = useQuestionsCountTextField({
    onAdd: append,
    onRemove: remove,
    getQuestionToAppend: getNextQuestion,
    questionsCount: fields.length,
    onChangeQuestionsCountTex: setQuestionsCountTextFieldValue,
  });

  return (
    <BaseExerciseQuestionsForm
      onAddQuestion={handleAddQuestion}
      initialQuestionNumber={initialQuestionNumber}
      onChangeInitialQuestionNumber={onChangeInitialQuestionNumber}
      onChangeQuestionCount={onChangeQuestionsCount}
      questionsCountText={questionsCountTextFieldValue}
    >
      {fields.map((field, index) => (
        <MultipleChoiceQuestionField
          key={field.id}
          index={index}
          onRemove={handleRemoveQuestionAtIndex}
          allOptionTypes={allOptionTypes}
          defaultOptionType={defaultOptionType}
          getNextOption={getNextOption}
          canAddMoreOptions={canAddMoreOptions}
        />
      ))}
    </BaseExerciseQuestionsForm>
  );
};

export default MultipleChoiceQuestionsForm;
